/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useContext } from "react";

import Label from "@/components/Label/Label";
import LanguageContext from "@/context/language/LanguageContext";

import PickerInputValue from "../../shared/components/PickerInputValue";
import { input, rootCSS } from "../../shared/styles/useDatePickerStyles";
import { VIEW_MODE } from "../../shared/types";
import DayView from "../DayRangePicker/components/DayView";
import WeekdayHeader from "../DayRangePicker/components/WeekdayHeader";
import BaseDayPickersPopover from "../shared/BaseDayPickersPopover";
import MonthSelectView from "../shared/MonthSelectView";
import TimeSection from "./components/TimeSection";
import { useSingleDayPicker } from "./hooks/useSingleDayPicker";
import { SingleDayPickerProps } from "./types";

const SingleDayPicker = ({
  minDate,
  maxDate,
  disabled,
  externalValue,
  customStyle,
  smallerInput,
  timeInput,
  nthDay,
  label,
  selectedTime,
  originalStartDay,
  isRequired,
  titleTooltip,
  shouldDisableDate,
  onChange,
  onClear,
  onAfterClear
}: SingleDayPickerProps) => {
  const { language } = useContext(LanguageContext);

  const {
    year,
    month,
    open,
    viewMode,
    viewVisible,
    slideClass,
    anchorRef,
    pickerRef,
    maxPickerHeight,
    selectedDay,
    noSelectedDay,
    hasClearableValue,
    isAtMinMonth,
    isAtMaxMonth,
    getHeaderLabel,
    openPicker,
    closePicker,
    handleDayClick,
    handleNavigateBack,
    handleNavigateForward,
    handleHeaderClick,
    handleMonthSelect,
    handleYearSelect,
    handleClear,
    cancelHoverClear,
    getDisplayValue
  } = useSingleDayPicker({
    minDate,
    maxDate,
    disabled,
    externalValue,
    timeInput,
    selectedTime,
    onChange,
    onAfterClear
  });

  const headerSlot =
    viewMode === VIEW_MODE.Day ? (
      <WeekdayHeader language={language} />
    ) : undefined;

  return (
    <>
      {label && (
        <Label
          titleLeft={label}
          isRequired={isRequired}
          titleTooltip={titleTooltip}
        />
      )}
      <div css={css(rootCSS(disabled), customStyle)}>
        <div
          onClick={openPicker}
          ref={anchorRef}
          css={css(input(open, disabled, smallerInput))}
          data-testid="single-date-picker-input-field"
        >
          <PickerInputValue
            displayValue={getDisplayValue()}
            isPlaceholder={noSelectedDay}
            disabled={disabled}
            hasClearableValue={hasClearableValue && !disabled}
            onClear={onClear ?? handleClear}
          />
        </div>

        <BaseDayPickersPopover
          open={open}
          anchorRef={anchorRef}
          pickerRef={pickerRef}
          viewMode={viewMode}
          viewVisible={viewVisible}
          slideClass={slideClass}
          year={year}
          maxPickerHeight={maxPickerHeight}
          isAtMinMonth={isAtMinMonth}
          isAtMaxMonth={isAtMaxMonth}
          getHeaderLabel={getHeaderLabel}
          onClose={closePicker}
          onHeaderClick={handleHeaderClick}
          onNavigateBack={handleNavigateBack}
          onNavigateForward={handleNavigateForward}
          onYearSelect={handleYearSelect}
          minDate={minDate}
          maxDate={maxDate}
          headerSlot={headerSlot}
          timeComponent={timeInput && <TimeSection timeInput={timeInput} />}
        >
          {viewMode === VIEW_MODE.Month ? (
            <MonthSelectView
              year={year}
              month={month}
              minDate={minDate}
              maxDate={maxDate}
              onMonthSelect={handleMonthSelect}
            />
          ) : (
            <DayView
              year={year}
              month={month}
              startDay={selectedDay}
              endDay={selectedDay}
              hoverDay={null}
              picking={null}
              minDate={minDate}
              maxDate={maxDate}
              isRangePicker={false}
              disableSameDay={false}
              nthDay={nthDay}
              originalStartDay={originalStartDay}
              shouldDisableDate={shouldDisableDate}
              onDayClick={handleDayClick}
              cancelHoverClear={cancelHoverClear}
              setHoverDay={() => {}}
            />
          )}
        </BaseDayPickersPopover>
      </div>
    </>
  );
};

export default SingleDayPicker;
